import React from 'react';
import {FormattedMessage} from 'react-intl';
import {observer} from 'mobx-react';
import {injectIntl} from 'react-intl';
import {ReactSelect, DropdownSelect, PanelTooltip, DocumentTitle, Spinner} from 'components';
import {t} from 'shared/core';
import {auth} from 'main/tools';
import PositionTitleView from 'recruiting/components/PositionTitleView';
import DeletePositionModal from '../directory/DeletePositionModal';
import Button from 'components/buttons/Button';
import {useModal} from 'shared/hooks';
import PromptModal from './PromptModal';
import DeletePromptModal from './DeletePromptModal';
import AiScreenerPrompt from 'stores/recruiting/AiScreenerPrompt';
import AiSparkle from 'img/ai-sparkle.svg';
import _ from 'lodash';

const StatusTooltip = observer(() => {
  return (
    <div>
      <div className='mb2'>
        <FormattedMessage id='recruiting.hints.position_status.header'/>
      </div>
      <ul>
        <li className='mb1'>
          <FormattedMessage
            id='recruiting.hints.position_status.PUBLISHED_BULLET'
            values={{
              publishedPositions: <span className='medium'><FormattedMessage id='recruiting.hints.position_status.published'/></span>
            }}/>
        </li>
        <li className='mb1'>
          <FormattedMessage
            id='recruiting.hints.position_status.INTERNAL_BULLET'
            values={{
              internalPositions: <span className='medium'><FormattedMessage id='recruiting.hints.position_status.internal'/></span>
            }}/>
        </li>
        <li>
          <FormattedMessage
            id='recruiting.hints.position_status.CLOSED_BULLET'
            values={{
              closedPositions: <span className='medium'><FormattedMessage id='recruiting.hints.position_status.closed'/></span>
            }}/>
        </li>
      </ul>
    </div>
  );
});

const PromptButtonContent = observer(({uiState}) => {
  const {position, atUsageLimit} = uiState;

  if (atUsageLimit) {
    return <div className='flex gap-1 align-items-middle'>
      <div style={{pointerEvents: 'unset'}}>
      <PanelTooltip
        icon={<i className='material-icons white'>{'error'}</i>}
        text={position.currentPrompt ? 'recruiting.hiring_funnel.AI_LIMIT_REACHED_WITH_PROMPT' : 'recruiting.hiring_funnel.AI_LIMIT_REACHED'}
      />
      </div>
      <div className='mr1'>
        <FormattedMessage id='recruiting.hiring_funnel.AI Screener'/>
      </div>
      <img src={AiSparkle} width='18px' height='18px' role='presentation'/>
    </div>;
  }

  if (!position.currentPrompt) {
    return <div className='flex gap-1 align-items-middle'>
      <div className='mr1'>
        <FormattedMessage id='recruiting.hiring_funnel.AI Screener'/>
      </div>
      <img src={AiSparkle} width='18px' height='18px' role='presentation'/>
    </div>;
  } else if (position.currentPrompt.paused) {
    return <div className='flex gap-1 align-items-middle'>
      <i className='material-icons'>{'pause'}</i>
      <div className='mr1'>
        <FormattedMessage id='recruiting.hiring_funnel.SCREENING_PAUSED' values={{progress: position.currentPrompt.progress}}/>
      </div>
      <img src={AiSparkle} width='18px' height='18px' role='presentation'/>
    </div>;
  } else if (position.currentPrompt.inProgress) {
    return <div className='flex gap-1 align-items-middle'>
      <Spinner size='Btn'/>
      <div className='mr1'>
        <FormattedMessage id='recruiting.hiring_funnel.SCREENING_IN_PROGRESS' values={{progress: position.currentPrompt.progress}}/>
      </div>
      <img src={AiSparkle} width='18px' height='18px' role='presentation'/>
    </div>;
  } else {
    return <div className='flex gap-1 align-items-middle'>
      <div className='mr1'>
        <FormattedMessage id='recruiting.hiring_funnel.SCREENING_ACTIVE' values={{progress: position.currentPrompt.progress}}/>
      </div>
      <img src={AiSparkle} width='18px' height='18px' role='presentation'/>
    </div>;
  }
});

const PromptButton = observer(({uiState}) => {
  const {position, atUsageLimit} = uiState;
  if (position.isClosed) return null;

  if (!position.currentPrompt || atUsageLimit) {
    return (
      <Button
        disabled={atUsageLimit}
        style={atUsageLimit ? {pointerEvents: 'unset', cursor: 'unset'} : {}}
        className='mr2'
        trait='magic'
        onClick={() => uiState.openPromptModal(position.currentPrompt)}
      >
        <PromptButtonContent uiState={uiState}/>
      </Button>
    );
  }

  return (
    <DropdownSelect className='mr2' buttonTrait='magic' selected={<PromptButtonContent uiState={uiState}/>} hideArrow>
      {!_.get(position, 'currentPrompt.paused') && <DropdownSelect.Option
        key={0}
        text={t('recruiting.hiring_funnel.Pause Screener')}
        icon='pause'
        onClick={() => uiState.pausePrompt(position.currentPrompt)}
      />}
      {_.get(position, 'currentPrompt.paused') && <DropdownSelect.Option
        key={1}
        text={t('recruiting.hiring_funnel.Resume Screener')}
        icon='play_arrow'
        onClick={() => uiState.resumePrompt(position.currentPrompt)}
      />}
      <DropdownSelect.Option
        key={2}
        text={t('recruiting.hiring_funnel.Edit Prompt')}
        icon='edit'
        onClick={() => uiState.openPromptModal(position.currentPrompt)}
      />
      <DropdownSelect.Option
        key={3}
        text={t('recruiting.hiring_funnel.Delete Prompt and Results')}
        icon='delete'
        onClick={() => uiState.openDeletePromptModal(position.currentPrompt)}
      />
    </DropdownSelect>
  );
});

const HiringFunnelSubNav = observer(({uiState, intl}) => {
  const {position} = uiState;
  const promptModalOpen = useModal(uiState, 'PromptModal', 'editingPrompt', AiScreenerPrompt);
  const deletePromptModalOpen = useModal(uiState, 'DeletePromptModal');

  return (
    <div className='Subnav Subnav--react'>
      {position.title && <DocumentTitle title={position.title}/>}
      <div className='Container'>
        <div className='flex align-items-middle'>
          <div className='flex-grow'>
            <div className='Subnav-header'>
              <PositionTitleView position={position}/>
            </div>
          </div>
          <div className='flex align-items-middle right-align'>
            {auth.hasAccess('::MANAGE_ATS') && <div className='mr2'>
              <PanelTooltip><StatusTooltip/></PanelTooltip>
              <ReactSelect selected={intl.formatMessage({id: `recruiting.hiring_funnel.${position.status}`})} className='inline-block'>
                <ReactSelect.Option active={position.isPublished}
                                    text={intl.formatMessage({id: 'recruiting.hiring_funnel.published'})}
                                    onClick={() => uiState.changePositionStatus('published')}/>
                <ReactSelect.Option active={position.isInternal}
                                    text={intl.formatMessage({id: 'recruiting.hiring_funnel.internal'})}
                                    onClick={() => uiState.changePositionStatus('internal')}/>
                <ReactSelect.Option active={position.isClosed}
                                    text={intl.formatMessage({id: 'recruiting.hiring_funnel.closed'})}
                                    onClick={() => uiState.changePositionStatus('closed')}/>
              </ReactSelect>
            </div>}
            {auth.hasAccess('::MANAGE_ATS') && !auth.company.demoAccount  && <PromptButton uiState={uiState}/>}
            <DropdownSelect selected={t('recruiting.hiring_funnel.More')}>
              {!position.isClosed && <DropdownSelect.Option
                key={1}
                text={intl.formatMessage({id: 'recruiting.hiring_funnel.Add Candidate'})}
                icon='person_add'
                onClick={() => uiState.history.push(`/positions/${position.id}/add-candidate`)}
              />}
              {position.showViewPosting &&
                <DropdownSelect.Option key={2} text={intl.formatMessage({id: 'recruiting.hiring_funnel.View Posting'})} icon='launch' onClick={() => uiState.goToPosting(position)}/>}
              {position.canPatch &&
                <DropdownSelect.Option key={3} text={intl.formatMessage({id: 'recruiting.hiring_funnel.Edit Posting'})} icon='edit' onClick={() => uiState.history.push(`/positions/${position.id}`)}/>}
              {position.canDelete &&
                <DropdownSelect.Option key={4} text={intl.formatMessage({id: 'recruiting.hiring_funnel.Delete Posting'})} icon='delete' onClick={() => uiState.openDeleteModal()}/>}
            </DropdownSelect>
          </div>
        </div>
      </div>
      <DeletePositionModal uiState={uiState}/>
      <PromptModal isOpen={promptModalOpen} uiState={uiState}/>
      <DeletePromptModal isOpen={deletePromptModalOpen} uiState={uiState}/>
    </div>
  );
});

export default injectIntl(HiringFunnelSubNav);
